
import { Vue, Component, Prop } from "vue-property-decorator";
import * as VueGoogleMaps from "vue2-google-maps";
import {
  Project,
  Project_Activity,
  User,
  Checkin_Status,
  Checkout_Status,
} from "@/generated/graphql";
import GET_CLOCK_STATUS_ENUM from "@/apollo/projectActivities/GetProjectActivityClockEnum.gql";
import UPDATE_PROJECT_ACTIVITY from "@/apollo/projectActivities/UpdateActivity.gql";
import ModalFullScreen from "@/components/modal/ModalFullScreenNew.vue";
import UiButton from "@/components/button/ui-button.vue";
import CrossIcon from "@/components/icons/CrossIconNew.vue";
import CrossIconHover from "@/components/icons/CrossIconHover.vue";
import ClockIcon from "@/components/icons/clockIconNew.vue";
import {
  GEO_COORDS_API,
  GEO_COORDS_API_KEY,
  VUE_GOOGLEMAP_API_KEY,
} from "@/data/constants";
import ClockIconBlack from "@/components/icons/Clock-icon-black.vue";
import Badge from "@/components/common/BadgeNew.vue";
import axios from "axios";
import Message from "@/data/notificationsMessages";
import { NOTIFICATION } from "@/entities/Events";
import getAuthData from "@/utils/getAuthData";
import moment from "moment";

Vue.use(VueGoogleMaps, {
  load: {
    key: VUE_GOOGLEMAP_API_KEY,
    v: "3",
    libraries: "places",
  },
});

enum ClockInStatus {
  ON_TIME = "ON_TIME",
  EARLY = "EARLY",
  LATE = "LATE",
  OUTSIDE = "OUTSIDE",
}

enum ClockType {
  IN = "IN",
  OUT = "OUT",
}
function calculateDistanceInMeters(lat1, lon1, lat2, lon2) {
  const R = 6371e3; // Earth's radius in meters
  const toRad = angle => (angle * Math.PI) / 180;

  const φ1 = toRad(lat1);
  const φ2 = toRad(lat2);
  const Δφ = toRad(lat2 - lat1);
  const Δλ = toRad(lon2 - lon1);

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) *
    Math.sin(Δλ / 2) * Math.sin(Δλ / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c; // distance in meters
}
@Component({
  components: {
    ModalFullScreen,
    Badge,
    CrossIcon,
    CrossIconHover,
    UiButton,
    ClockIcon,
    ClockIconBlack,
  },
})
export default class ContractorClockInModal extends Vue {
  @Prop({ type: Object, required: true }) readonly project!: Project;
  @Prop({ type: Object, required: true }) readonly activity!: Project_Activity;
  @Prop({ type: Object, required: true }) readonly currentUser!: User;

  DIST_ALLOWED_FROM_CURRENT_LOC = 70;
  isHovered: boolean = false;
  clockInHovering = false;
  processing: boolean = false;
  currentLocation = { lat: null, lng: null };
  passedLocation = { lat: null, lng: null };
  customIcons = {
    current: {
      url: `/images/clockInCurrent.svg`,
      scaledSize: { width: 20, height: 20 },
    },
    passed: {
      url: `/images/clockInUser.svg`,
      scaledSize: { width: 20, height: 20 },
    },
  };

  distanceFromCurrentLocation = null;
  infoWindow = "current";
  googleMapScriptLoaded = false;
  placesService = null;

  mounted() {
    // Only get current location for in-person visits with geofence enabled
    if (this.shouldShowGeofenceMap) {
      this.getCurrentLocation();
    }
  }

  get isVirtualVisit(): boolean {
    return this.activity.virtual === true;
  }

  get shouldCheckGeofence(): boolean {
    // Check geofence if: NOT virtual AND check_geo is not explicitly false
    return !this.isVirtualVisit && this.activity.check_geo !== false;
  }

  get shouldShowGeofenceMap(): boolean {
    // Show map only for in-person visits with geofence enabled
    return this.shouldCheckGeofence;
  }

  get isStartTimePassed() {
    if (this.activity.checkin_status) {
      return false;
    }

    // Convert start time (UTC) to local time
    const startTimeLocal = moment.utc(this.activity.start_time).local();

    // Add 24 hours to the start time
    const startTimePlus24Hours = startTimeLocal.clone().add(24, 'hours');

    // Get current local time
    const now = moment();

    // Check if current time is after (start time + 24 hours)
    return now.isAfter(startTimePlus24Hours);
  }
  get isCurrentClockType() {
    if (this.activity.checkin_status) {
      return ClockType.OUT;
    }
    return ClockType.IN;
  }

  get currentClockInOutStatus() {
    // Only check geofence for in-person visits with geofence enabled
    const tooFar = this.shouldCheckGeofence && this.distanceFromCurrentLocation !== null && this.distanceFromCurrentLocation > this.DIST_ALLOWED_FROM_CURRENT_LOC;
    if (tooFar) return ClockInStatus.OUTSIDE;

    const now = moment().utc();

    if (this.isCurrentClockType === "OUT") {
      // Clock-Out: check against scheduled end time
      const end = moment.utc(this.activity.end_time);
      const graceEnd = end.clone().add(5, "minutes");

      if (now.isBefore(end)) return ClockInStatus.EARLY;
      if (now.isBefore(graceEnd)) return ClockInStatus.ON_TIME;
      return ClockInStatus.LATE;
    } else {
      // Clock-In: New timing logic
      const start = moment.utc(this.activity.start_time);
      const earlyBuffer = start.clone().subtract(10, "minutes"); // 10 minutes before start
      const lateBuffer = start.clone().subtract(5, "minutes"); // 5 minutes before start

      if (now.isBefore(earlyBuffer)) return ClockInStatus.EARLY; // More than 10 min early
      if (now.isBefore(lateBuffer)) return ClockInStatus.ON_TIME; // 10 min to 5 min before (on time)
      return ClockInStatus.LATE; // 5 min before start onwards (late)
    }
  }

  get currentClockinDate() {
    return this.formattedDate(new Date().toString());
  }

  get currentClockinTime() {
    return this.formattedTime(new Date().toString());
  }

  get clockinStartTime() {
    if (this.activity.start_time) {
      return moment(this.activity.start_time)
        .subtract(10, "minutes")
        .format("h:mm A");
    }
    return "";
  }
  get clockinEndTime() {
    if (this.activity.start_time) {
      return moment(this.activity.start_time)
        .subtract(5, "minutes")
        .format("h:mm A");
    }
    return "";
  }

  get clockoutEndTime() {
    if (this.activity.end_time) {
      return moment(this.activity.end_time).add(5, "minutes").format("h:mm A");
    }
    return "";
  }

  get isWithinClockInWindow() {
    // If time alerts are disabled, allow clock-in anytime
    if (this.activity.disable_time_alerts === true) return true;

    if (!this.activity.start_time || !this.activity.start_date || !this.activity.end_time) return false;

    const now = moment().utc();
    const startDate = moment.utc(this.activity.start_date);
    const endTime = moment.utc(this.activity.end_time);

    // Allow clock-in from any time on the visit day until end time
    if (now.isSameOrBefore(startDate, 'day')) {
      return now.isSameOrBefore(endTime);
    }
    return false;
  }

  get clockInDisabledMessage() {
    if (this.isCurrentClockType !== "IN" || this.isWithinClockInWindow) return "";

    return "Clock-in window has passed";
  }

  getCurrentLocation() {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position) => {
        this.currentLocation = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };

        if (this.activity.location) {
          this.selectLocation(this.activity.location);
        }
      });

    }
  }

  calculateDistance() {

    const dis = calculateDistanceInMeters(this.passedLocation.lat, this.passedLocation.lng, this.currentLocation.lat, this.currentLocation.lng) // In Meters
    this.distanceFromCurrentLocation = dis;
  }

  toggleInfo(marker) {
    this.infoWindow = this.infoWindow === marker ? null : marker;
  }

  formattedDate(date: string): string {
    if (date) {
      return moment(date).format("ddd DD MMM, YYYY");
    } else {
      return "";
    }
  }

  formattedTime(dateTime: string): string {
    if (dateTime) {
      return moment(dateTime).format("h:mm A");
    } else {
      return "";
    }
  }
  async onClockIn() {
    this.processing = true;
    const startTime = moment(this.activity.start_time);
    const earlyBuffer = moment(startTime).subtract(10, "minutes"); // 10 minutes before start
    const lateBuffer = moment(startTime).subtract(5, "minutes"); // 5 minutes before start
    const currentTime = moment();

    // Determine status based on new timing logic
    let clockinStatus;
    // Only check geofence for in-person visits with geofence enabled
    if (this.shouldCheckGeofence && this.distanceFromCurrentLocation > this.DIST_ALLOWED_FROM_CURRENT_LOC) {
      clockinStatus = ClockInStatus.OUTSIDE;
    } else if (currentTime.isBefore(earlyBuffer)) {
      clockinStatus = ClockInStatus.EARLY; // More than 10 min early
    } else if (currentTime.isBefore(lateBuffer)) {
      clockinStatus = ClockInStatus.ON_TIME; // 10 min to 5 min before (on time)
    } else {
      clockinStatus = ClockInStatus.LATE; // 5 min before start onwards (late)
    }
    const finalStatus = clockinStatus;

    // 4. Fix email triggers - respect notification flags
    try {
      // Only send geofence/location emails if check_geo is enabled
      if (clockinStatus === ClockInStatus.OUTSIDE && this.shouldCheckGeofence) {
        console.log("Clock-in outside geofence, triggering email");
        await this.triggerOutsideClockInEmail();
      }

      // Only send time-based emails if disable_time_alerts is NOT true
      if (this.activity.disable_time_alerts !== true) {
        if (clockinStatus === ClockInStatus.LATE) {
          console.log("Clock-in late, triggering email");
          await this.triggerLateClockInEmail();
        } else if (clockinStatus === ClockInStatus.EARLY) {
          console.log("Clock-in early, triggering email");
          await this.triggerEarlyClockInEmail();
        }
      } else {
        console.log("Time alerts disabled for this activity, skipping time-based email");
      }
    } catch (error) {
      this.processing = false;
      console.error("Email trigger failed:", error);
    }

    // 5. Save to backend
    try {
      await this.$apollo.mutate({
        mutation: UPDATE_PROJECT_ACTIVITY,
        variables: {
          id: this.activity.id,
          set: {
            checkin_status: finalStatus,
            checkin_time: currentTime.utc().format("YYYY-MM-DDTHH:mm:ss+00:00"),
          },
        },
      });
      this.processing = false;
      this.closeModal();
      this.$emit("updateParent");
      this.$eventBus.emit(
        NOTIFICATION,
        Message.successMessage(
          `Clocked-in as ${clockinStatus.toLowerCase()} successfully`
        )
      );
    } catch (error) {
      console.error("Clock-in failed:", error);
      this.$eventBus.emit(
        NOTIFICATION,
        Message.errorMessage("Failed to record clock-in")
      );
    }
  }

  async onClockOut() {
    let clockoutStatus = this.currentClockInOutStatus;
    this.processing = true;
    const nowUtc = moment.utc();

    // Only send geofence/location emails if check_geo is enabled
    if (this.currentClockInOutStatus === ClockInStatus.OUTSIDE && this.shouldCheckGeofence) {
      try {
        await this.triggerOutsideClockOutEmail();
      } catch (error) {
        console.error(error);
      }
    }

    // Only send time-based emails if disable_time_alerts is NOT true
    if (this.activity.disable_time_alerts !== true) {
      if (clockoutStatus === ClockInStatus.LATE) {
        try {
          await this.triggerLateClockOutEmail();
        } catch (error) {
          console.error(error);
        }
      } else if (clockoutStatus === ClockInStatus.EARLY) {
        try {
          await this.triggeEarlyClockOutEmail();
        } catch (error) {
          console.error(error);
        }
      }
    } else {
      console.log("Time alerts disabled for this activity, skipping time-based email");
    }
    try {
      await this.$apollo.mutate({
        mutation: UPDATE_PROJECT_ACTIVITY,
        variables: {
          id: this.activity.id,
          set: {
            checkout_status: clockoutStatus,
            checkout_time: nowUtc.format("YYYY-MM-DDTHH:mm:ss+00:00"),
            completed: true,
          },
        },
      });
      this.$eventBus.emit(
        NOTIFICATION,
        Message.successMessage("Clocked-out successfully.")
      );
      this.processing = true;
      this.closeModal();
      this.$apollo.queries.activities.refetch();
    } catch (error) {
      console.error(error);
    }
  }

  async triggerLateClockInEmail() {
    const uri = `${process.env.VUE_APP_LAMBDA}/send-clockinout-notification`;
    const { headers } = await getAuthData();
    const { data }: any = await axios.post(
      uri,
      {
        data: {
          project: this.project,
          activity: this.activity,
          clockStatus: ClockType.IN,
        },
      },
      { headers }
    );
  }

  async triggerLateClockOutEmail() {
    const uri = `${process.env.VUE_APP_LAMBDA}/send-clockinout-notification`;
    const { headers } = await getAuthData();
    const { data }: any = await axios.post(
      uri,
      {
        data: {
          project: this.project,
          activity: this.activity,
          clockStatus: ClockType.OUT,
        },
      },
      { headers }
    );
  }
  async triggerEarlyClockInEmail() {
    const uri = `${process.env.VUE_APP_LAMBDA}/send-clockinout-notification`;
    const { headers } = await getAuthData();
    const { data }: any = await axios.post(
      uri,
      {
        data: {
          project: this.project,
          activity: this.activity,
          clockStatus: ClockType.IN,
          timingType: "EARLY",
        },
      },
      { headers }
    );
  }

  async triggeEarlyClockOutEmail() {
    const uri = `${process.env.VUE_APP_LAMBDA}/send-clockinout-notification`;
    const { headers } = await getAuthData();
    const { data }: any = await axios.post(
      uri,
      {
        data: {
          project: this.project,
          activity: this.activity,
          clockStatus: ClockType.OUT,
          timingType: "EARLY",
        },
      },
      { headers }
    );
  }

  async triggerOutsideClockInEmail() {
    const uri = `${process.env.VUE_APP_LAMBDA}/send-wrong-location-clockinout-notification`;
    const { headers } = await getAuthData();
    const { data }: any = await axios.post(
      uri,
      {
        data: {
          project: this.project,
          activity: this.activity,
          clockStatus: ClockType.IN,
        },
      },
      { headers }
    );
  }
  async triggerOutsideClockOutEmail() {
    const uri = `${process.env.VUE_APP_LAMBDA}/send-wrong-location-clockinout-notification`;
    const { headers } = await getAuthData();
    const { data }: any = await axios.post(
      uri,
      {
        data: {
          project: this.project,
          activity: this.activity,
          clockStatus: ClockType.OUT,
        },
      },
      { headers }
    );
  }

  async fetchCoordinates(address: string) {
    const params = {
      q: address,
      apiKey: GEO_COORDS_API_KEY,
    };

    try {
      const response = await axios.get(GEO_COORDS_API, {
        params: params,
      });

      if (
        response.data.items[0]?.position?.lat !== null &&
        response.data.items[0]?.position?.lng !== null
      ) {
        return response.data.items[0]?.position;
      } else {
        return null;
      }
    } catch (error) {
      throw error;
    }
  }

  async selectLocation(location) {
    try {
      const position = await this.fetchCoordinates(location);
      if (position?.lat) {
        this.passedLocation = { lat: position?.lat, lng: position?.lng };
        this.calculateDistance();
      }
    } catch (error) {
      console.error(error);
    }
  }
  closeModal() {
    this.$emit("close");
  }
}
